import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { UserRole, setUserRole, UserRoleRecord } from '@/services/roleManager';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useClerkAuth';
import { Shield, UserCheck, AlertCircle } from 'lucide-react';

export default function RoleManager() {
  const { user, canManageRoles, refreshRole } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [userRoles, setUserRoles] = useState<UserRoleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<UserRole>('READER');

  // Load all user roles
  useEffect(() => {
    const loadUserRoles = async () => {
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error loading user roles:', error);
          toast({
            title: t('roleManager.error'),
            description: t('roleManager.failedToLoadRoles'),
            variant: 'destructive',
          });
          return;
        }

        setUserRoles(
          (data || []).filter(role => role.role !== null) as UserRoleRecord[]
        );
      } catch (error) {
        console.error('Error in loadUserRoles:', error);
      } finally {
        setLoading(false);
      }
    };

    if (canManageRoles()) {
      loadUserRoles();
    }
  }, [canManageRoles, toast]);

  const handleAssignRole = async (userId: string, role: UserRole) => {
    if (!user?.id) return;

    setAssigning(true);
    try {
      const success = await setUserRole(userId, role, user.id);

      if (success) {
        toast({
          title: t('roleManager.success'),
          description: t('roleManager.roleUpdatedSuccess', { role }),
        });

        // Refresh the user roles list
        const { data } = await supabase
          .from('user_roles')
          .select('*')
          .order('created_at', { ascending: false });

        if (data) {
          setUserRoles(
            data.filter(role => role.role !== null) as UserRoleRecord[]
          );
        }

        // If updating own role, refresh the auth context
        if (userId === user.id) {
          await refreshRole();
        }
      } else {
        toast({
          title: t('roleManager.error'),
          description: t('roleManager.failedToUpdateRole'),
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      toast({
        title: t('roleManager.error'),
        description: t('roleManager.failedToUpdateRole'),
        variant: 'destructive',
      });
    } finally {
      setAssigning(false);
    }
  };

  const handleCreateUserRole = async () => {
    if (!newUserEmail.trim() || !user?.id) return;

    setAssigning(true);
    try {
      // In a real app, you'd first look up the user by email from Clerk
      // For now, we'll assume the email is the user ID
      const success = await setUserRole(
        newUserEmail.trim(),
        newUserRole,
        user.id
      );

      if (success) {
        toast({
          title: t('roleManager.success'),
          description: t('roleManager.roleAssignedSuccess', {
            role: newUserRole,
            email: newUserEmail,
          }),
        });

        // Refresh the user roles list
        const { data } = await supabase
          .from('user_roles')
          .select('*')
          .order('created_at', { ascending: false });

        if (data) {
          setUserRoles(
            data.filter(role => role.role !== null) as UserRoleRecord[]
          );
        }

        setNewUserEmail('');
        setNewUserRole('READER');
      } else {
        toast({
          title: t('roleManager.error'),
          description: t('roleManager.failedToAssignRole'),
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error creating user role:', error);
      toast({
        title: t('roleManager.error'),
        description: t('roleManager.failedToAssignRole'),
        variant: 'destructive',
      });
    } finally {
      setAssigning(false);
    }
  };

  if (!canManageRoles()) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{t('roleManager.noPermission')}</AlertDescription>
      </Alert>
    );
  }

  const getRoleBadgeVariant = (role: UserRole) => {
    switch (role) {
      case 'ADMIN':
        return 'destructive';
      case 'EDITOR':
        return 'default';
      case 'READER':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {t('roleManager.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Create New User Role */}
          <div className="space-y-4 p-4 border rounded-lg">
            <h4 className="font-semibold">
              {t('roleManager.assignRoleToUser')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="user-email">
                  {t('roleManager.userEmailId')}
                </Label>
                <Input
                  id="user-email"
                  placeholder={t('roleManager.userEmailPlaceholder')}
                  value={newUserEmail}
                  onChange={e => setNewUserEmail(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="role-select">{t('roleManager.role')}</Label>
                <Select
                  value={newUserRole}
                  onValueChange={(value: UserRole) => setNewUserRole(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="READER">
                      {t('roleManager.reader')}
                    </SelectItem>
                    <SelectItem value="EDITOR">
                      {t('roleManager.editor')}
                    </SelectItem>
                    <SelectItem value="ADMIN">
                      {t('roleManager.admin')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleCreateUserRole}
                  disabled={!newUserEmail.trim() || assigning}
                  className="w-full"
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  {t('roleManager.assignRole')}
                </Button>
              </div>
            </div>
          </div>

          {/* Current User Roles */}
          <div>
            <h4 className="font-semibold mb-4">
              {t('roleManager.currentUserRoles')}
            </h4>
            {loading ? (
              <p>{t('roleManager.loadingRoles')}</p>
            ) : userRoles.length === 0 ? (
              <p className="text-muted-foreground">
                {t('roleManager.noRolesFound')}
              </p>
            ) : (
              <div className="space-y-3">
                {userRoles.map(roleRecord => (
                  <div
                    key={roleRecord.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium">{roleRecord.user_id}</p>
                        <p className="text-sm text-muted-foreground">
                          {t('roleManager.assigned')}{' '}
                          {new Date(
                            roleRecord.assigned_at
                          ).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant={getRoleBadgeVariant(roleRecord.role)}>
                        {roleRecord.role}
                      </Badge>
                    </div>

                    <Select
                      value={roleRecord.role}
                      onValueChange={(value: UserRole) =>
                        handleAssignRole(roleRecord.user_id, value)
                      }
                      disabled={assigning}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="READER">
                          {t('roleManager.reader')}
                        </SelectItem>
                        <SelectItem value="EDITOR">
                          {t('roleManager.editor')}
                        </SelectItem>
                        <SelectItem value="ADMIN">
                          {t('roleManager.admin')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
